import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridPinnedColumnFields,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { TruncatedTooltipCell } from "../TruncatedTooltipCell";
import {
  deleteOtoPlan,
  updateOtoPlanActive,
  type OtoPlan,
} from "../../../services/objectToObjectPlanService";
import { OBJECT_TO_OBJECT_PRIVILEGES } from "../../../services/permissionsService";
import AiPlanActiveSwitch from "../AiPlanActiveSwitch";
import DeleteTaggingPlanConfirmDialog from "../TaggingPlan/DeleteTaggingPlanConfirmDialog";
import OtoReferenceItemsCell, {
  buildOtoImpactItemDetails,
  buildOtoReferenceItemDetails,
  OtoImpactItemsCell,
} from "./OtoReferenceItemsCell";
import { formatReferenceItemsRnLabel } from "../TaggingPlan/ReferenceItemsCell";
import TaggingPlanActiveConfirmDialog, {
  OBJECT_TO_OBJECT_PLAN_ACTIVE_COPY,
  type TaggingPlanActiveConfirmMode,
} from "../TaggingPlan/TaggingPlanActiveConfirmDialog";
import TaggingPlanRowActions from "../TaggingPlan/TaggingPlanRowActions";
import ObjectToObjectPlanTableToolbar from "./ObjectToObjectPlanTableToolbar";

type ObjectToObjectPlanTableProps = Readonly<{
  rows: OtoPlan[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onAddNew?: () => void;
  onEdit: (row: OtoPlan) => void;
  onPlanDeleted?: () => void;
  onPlanUpdated?: () => void;
}>;

function OtoNoRowsOverlay({ onAddNew }: { onAddNew?: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No data available.{" "}
        {onAddNew ? (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onAddNew}
            sx={{
              color: "#295EF1",
              fontSize: "14px",
              fontWeight: 500,
              verticalAlign: "baseline",
              cursor: "pointer",
            }}
          >
            Add Now
          </Link>
        ) : null}
      </Typography>
    </Box>
  );
}

export default function ObjectToObjectPlanTable({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  onAddNew,
  onEdit,
  onPlanDeleted,
  onPlanUpdated,
}: ObjectToObjectPlanTableProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { showMessage } = useSnackbar();
  const canManageOtoPlan = useHasPrivilege(OBJECT_TO_OBJECT_PRIVILEGES.manage);
  const [deleteTarget, setDeleteTarget] = useState<OtoPlan | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activeToggleTarget, setActiveToggleTarget] = useState<{
    row: OtoPlan;
    nextActive: boolean;
  } | null>(null);
  const [updatingActive, setUpdatingActive] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const manageColumnsPanelScrollTopRef = useRef(0);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const noRowsOverlaySlot = useCallback(
    () => (
      <OtoNoRowsOverlay onAddNew={canManageOtoPlan ? onAddNew : undefined} />
    ),
    [canManageOtoPlan, onAddNew],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deleteOtoPlan(deleteTarget.id);
      showMessage("Object to Object deleted successfully.", "success");
      setDeleteTarget(null);
      onPlanDeleted?.();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to delete Object to Object.",
        "error",
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, onPlanDeleted, showMessage]);

  const activeConfirmMode: TaggingPlanActiveConfirmMode | null =
    activeToggleTarget
      ? activeToggleTarget.nextActive
        ? "activate"
        : "deactivate"
      : null;

  const handleConfirmActiveToggle = useCallback(async () => {
    if (!activeToggleTarget || updatingActive) return;
    const { row, nextActive } = activeToggleTarget;
    setUpdatingActive(true);
    try {
      await updateOtoPlanActive(row.id, nextActive);
      showMessage(
        nextActive
          ? "Plan activated successfully"
          : "Plan deactivated successfully",
        "success",
      );
      setActiveToggleTarget(null);
      onPlanUpdated?.();
    } catch (error) {
      showMessage(
        error instanceof Error
          ? error.message
          : "Failed to update plan status.",
        "error",
      );
    } finally {
      setUpdatingActive(false);
    }
  }, [activeToggleTarget, onPlanUpdated, showMessage, updatingActive]);

  const columns = useMemo<GridColDef<OtoPlan>[]>(
    () => [
      {
        field: "id",
        headerName: "ID",
        width: 130,
        sortable: false,
        filterable: false,
      },
      {
        field: "name",
        headerName: "Assessment Name",
        flex: 1,
        minWidth: 160,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.name?.trim() || "",
        renderCell: ({ row }) => {
          const text = row.name?.trim() || "";
          if (!text) {
            return (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#8c8c8c" }}>
                  -
                </Typography>
              </Box>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                width: "100%",
                minWidth: 0,
              }}
            >
              <TruncatedTooltipCell text={text} />
            </Box>
          );
        },
      },
      {
        // Same I1/I2 dashed + hover style as Reference Items (R1/R2).
        field: "impactItems",
        headerName: "Impact Items",
        flex: 1,
        minWidth: 140,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) =>
          formatReferenceItemsRnLabel(buildOtoImpactItemDetails(row)),
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              minWidth: 0,
            }}
          >
            <OtoImpactItemsCell plan={row} />
          </Box>
        ),
      },
      {
        // After Impact Items — same R1/R2 dashed + hover style as Classification.
        field: "referenceItems",
        headerName: "Reference Items",
        flex: 1,
        minWidth: 140,
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) =>
          formatReferenceItemsRnLabel(buildOtoReferenceItemDetails(row)),
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
              minWidth: 0,
            }}
          >
            <OtoReferenceItemsCell plan={row} />
          </Box>
        ),
      },
      {
        field: "active",
        headerName: "Active",
        width: 88,
        sortable: false,
        filterable: false,
        resizable: false,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-plan-status-cell",
        renderCell: ({ row }) => (
          <AiPlanActiveSwitch
            checked={row.active}
            disabled={!canManageOtoPlan}
            onChange={(nextChecked) =>
              setActiveToggleTarget({ row, nextActive: nextChecked })
            }
          />
        ),
      },
      ...(canManageOtoPlan
        ? [
            {
              field: "actions",
              headerName: "Actions",
              width: 88,
              sortable: false,
              filterable: false,
              resizable: false,
              align: "center" as const,
              headerAlign: "center" as const,
              cellClassName: "ai-plan-actions-cell",
              renderCell: ({ row }: { row: OtoPlan }) => (
                <TaggingPlanRowActions
                  onEdit={() => onEdit(row)}
                  onDelete={() => setDeleteTarget(row)}
                />
              ),
            } satisfies GridColDef<OtoPlan>,
          ]
        : []),
    ],
    [canManageOtoPlan, onEdit],
  );

  const isEmpty = rows.length === 0;

  // Match Tagging Plan: Active then Actions (left → right). Controlled prop
  // so order is not stuck on a stale initialState after HMR / privilege load.
  const pinnedColumns = useMemo<GridPinnedColumnFields>(
    () => ({
      right: canManageOtoPlan ? ["active", "actions"] : ["active"],
    }),
    [canManageOtoPlan],
  );

  return (
    <>
      <Box
        className={`${styles.dataGridPanel}${
          isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
        }`}
        sx={{
          ...aiPlanDataGridPanelSx,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 480,
        }}
      >
        <DataGridPremium
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={48}
          columnHeaderHeight={48}
          disableRowSelectionOnClick
          disableRowGrouping
          disableAggregation
          disablePivoting
          disableColumnSorting
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[5, 10, 20, 50]}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          showToolbar
          showColumnVerticalBorder={false}
          disableColumnPinning={false}
          pinnedColumnsSectionSeparator="border"
          pinnedColumns={pinnedColumns}
          slots={{
            toolbar: ObjectToObjectPlanTableToolbar,
            noRowsOverlay: noRowsOverlaySlot,
            columnsManagement: columnsManagementSlot,
          }}
          slotProps={{
            toolbar: {},
          }}
          sx={
            [
              getRegulationsListDataGridSx(primaryColor),
              ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
              {
                height: "100%",
                minHeight: 400,
                "& .ai-plan-status-cell, & .ai-plan-actions-cell": {
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  "& .MuiDataGrid-cellContent": {
                    display: "flex !important",
                    alignItems: "center !important",
                    justifyContent: "center !important",
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                  },
                },
                "& .ai-plan-actions-cell.MuiDataGrid-cell--pinnedRight": {
                  boxShadow: "none",
                },
                "& .ai-plan-status-cell.MuiDataGrid-cell--pinnedRight": {
                  boxShadow: "none",
                },
              },
            ] as SxProps<Theme>
          }
        />
      </Box>

      <DeleteTaggingPlanConfirmDialog
        open={Boolean(deleteTarget)}
        deleting={deleting}
        description="This object to object plan will be deleted and will no longer be used for future matching. Existing results generated by this plan will not be removed."
        onClose={() => {
          if (!deleting) setDeleteTarget(null);
        }}
        onConfirm={handleConfirmDelete}
      />

      <TaggingPlanActiveConfirmDialog
        open={Boolean(activeToggleTarget)}
        mode={activeConfirmMode}
        saving={updatingActive}
        copyByMode={OBJECT_TO_OBJECT_PLAN_ACTIVE_COPY}
        onClose={() => {
          if (!updatingActive) setActiveToggleTarget(null);
        }}
        onConfirm={handleConfirmActiveToggle}
      />
    </>
  );
}
